import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Battery, Gauge, Scale, ArrowRight, Wallet, Calendar, Gift, CreditCard, Shield, PenTool as Tool, FileText, CheckCircle, Star, Tag, Zap, Truck, Briefcase, User, Award, Clock } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import SimulationForm from '../forms/SimulationForm';
import { MOTORCYCLE_MODELS } from '../../constants/products';

export const ProductShowcase: React.FC = () => {
  const [showSimulation, setShowSimulation] = useState(false);
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const features = [
    {
      icon: <Battery className="w-8 h-8 sm:w-12 sm:h-12 text-primary-500" />,
      title: "Autonomie Exceptionnelle",
      description: "Jusqu'à 120 km d'autonomie pour une liberté totale"
    },
    {
      icon: <Gauge className="w-8 h-8 sm:w-12 sm:h-12 text-primary-500" />,
      title: "Performance Optimale",
      description: "Vitesse et accélération adaptées à vos besoins"
    },
    {
      icon: <Scale className="w-8 h-8 sm:w-12 sm:h-12 text-primary-500" />,
      title: "Design Ergonomique",
      description: "Confort et maniabilité pour tous les trajets"
    }
  ];

  const payAsYouGoFeatures = [
    {
      icon: <Wallet className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Paiement Flexible",
      description: "Choisissez votre acompte et la durée de paiement qui vous convient"
    },
    {
      icon: <Calendar className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Premier Mois Gratuit",
      description: "Profitez d'un mois d'essai gratuit pour démarrer sereinement"
    },
    {
      icon: <Gift className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Pack Tout Inclus",
      description: "Assurance, maintenance et équipements de sécurité inclus"
    },
    {
      icon: <CreditCard className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Acompte Minimum",
      description: "Démarrez avec seulement 150 000 FCFA d'acompte"
    }
  ];

  const categoryIcons = {
    'PRO': <Briefcase className="w-5 h-5" />,
    'PARTICULIER': <User className="w-5 h-5" />
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {/* Products Section */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4 sm:mb-6 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
            NOS MOTOS ÉLECTRIQUES
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Découvrez notre gamme de motos électriques conçues pour répondre à tous vos besoins de mobilité.
            Des solutions durables, performantes et adaptées au contexte africain.
          </p>
        </div>

        {/* New Product Card Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 lg:gap-10 mb-12 sm:mb-16 md:mb-20">
          {MOTORCYCLE_MODELS.map((model) => (
            <div 
              key={model.id} 
              className="group relative overflow-hidden rounded-2xl shadow-xl transition-all duration-500 hover:shadow-2xl"
              onMouseEnter={() => setHoveredModel(model.id)}
              onMouseLeave={() => setHoveredModel(null)}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${model.color} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
              
              {/* Image */}
              <div className="relative h-56 sm:h-64 md:h-72 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                {model.image ? (
                  <img
                    src={model.image}
                    alt={`Moto électrique ${model.name} - ${model.description}`}
                    loading="lazy"
                    width="400"
                    height="300"
                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110 p-4"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                {/* Fallback si l'image ne charge pas */}
                <div className="flex items-center justify-center h-full" style={{ display: model.image ? 'none' : 'flex' }}>
                  <div className="text-center">
                    <div className="text-4xl sm:text-6xl font-bold text-primary-300 mb-2">{model.name[0]}</div>
                    <p className="text-sm text-gray-500">Image non disponible</p>
                  </div>
                </div>
                
                {/* Top Section */}
                <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex justify-between items-start">
                  {/* Category Tag */}
                  <div className="px-2 py-1 sm:px-3 sm:py-1 rounded-full text-white font-medium text-xs sm:text-sm shadow-md flex items-center gap-1 sm:gap-1.5 z-10">
                    <div className={`bg-gradient-to-r ${model.color} px-2 py-1 sm:px-3 sm:py-1 rounded-full flex items-center gap-1 sm:gap-1.5`}>
                      {categoryIcons[model.category as keyof typeof categoryIcons]}
                      {model.category}
                    </div>
                  </div>

                  {/* Price Tag */}
                  <div className="bg-white/90 backdrop-blur-sm px-2 py-1 sm:px-4 sm:py-2 rounded-full shadow-md">
                    <span className="font-bold text-primary-600 text-sm sm:text-base">{formatCurrency(model.basePrice)}</span>
                  </div>
                </div>
                
                {/* Bottom Section - Only visible on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
                    {model.highlights.map((highlight, idx) => (
                      <span key={idx} className="px-2 py-0.5 sm:px-3 sm:py-1 bg-white/80 backdrop-blur-sm text-primary-700 rounded-full text-xs sm:text-sm font-medium">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 sm:p-6 bg-white">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2 group-hover:text-primary-600 transition-colors">{model.name}</h3>
                <p className="text-gray-600 mb-3 line-clamp-2 text-sm sm:text-base">{model.description}</p>

                {/* Payment Options */}
                {model.paymentOptions && (
                  <div className="mb-3 pb-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-1.5 font-medium">Modalités de paiement :</p>
                    <div className="flex flex-wrap gap-1">
                      {model.paymentOptions.map((option: string, idx: number) => (
                        <span key={idx} className="inline-block bg-primary-50 text-primary-700 px-2 py-0.5 rounded text-xs font-medium">
                          {option}
                        </span>
                      ))}
                    </div>
                    {model.paymentNote && (
                      <p className="text-xs text-primary-600 font-semibold mt-1.5">
                        {model.paymentNote}
                      </p>
                    )}
                  </div>
                )}

                {/* Subtitle for Teranga+ */}
                {model.subtitle && (
                  <p className="text-xs italic text-gray-500 mb-3 bg-purple-50 px-3 py-2 rounded-lg border border-purple-100">
                    {model.subtitle}
                  </p>
                )}

                <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
                  {Object.entries(model.specs)
                    .filter(([key]) => ['autonomie', 'vitesse', 'poids'].includes(key))
                    .map(([key, value]) => (
                      <div key={key} className="flex flex-col items-center text-center group">
                        <div className="mb-1 sm:mb-3 transform group-hover:scale-110 transition-transform p-1 sm:p-2 bg-primary-50 rounded-full">
                          {key === 'autonomie' && <Battery className="text-primary-500" size={18} />}
                          {key === 'vitesse' && <Gauge className="text-primary-500" size={18} />}
                          {key === 'poids' && <Scale className="text-primary-500" size={18} />}
                        </div>
                        <span className="text-xs sm:text-base font-medium text-gray-900">{value}</span>
                        <span className="text-xs text-gray-500 capitalize">{key}</span>
                      </div>
                    ))}
                </div>

                <Link 
                  to={`/products/${model.id}`}
                  className="w-full inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg transform hover:translate-y-[-2px] text-sm sm:text-base"
                >
                  En savoir plus
                  <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
              
              {/* Absolute Link for the entire card */}
              <Link 
                to={`/products/${model.id}`}
                className="absolute inset-0 z-0"
                aria-hidden="true"
              />
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-12 sm:mb-16">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start p-4 sm:p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 hover:border-primary-200 transform hover:translate-y-[-4px] transition-all duration-300">
              <div className="flex-shrink-0 mr-3 sm:mr-4 p-2 sm:p-3 bg-primary-50 rounded-xl">
                {feature.icon}
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pay As You Go Section */}
        <div className="bg-gradient-to-br from-primary-800 to-primary-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 mb-12 sm:mb-16 text-white relative overflow-hidden transform transition-all duration-500 hover:shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
          
          <div className="relative">
            <div className="max-w-3xl mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-white via-gray-100 to-accent-200 bg-clip-text text-transparent">
                Pay As You Go
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed">
                Accédez à votre moto électrique avec notre solution de paiement flexible. 
                Choisissez votre modèle, personnalisez votre plan et commencez à rouler dès aujourd'hui.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {payAsYouGoFeatures.map((feature, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 sm:p-6 border border-white/20 transform transition-all duration-300 hover:scale-105 hover:bg-white/15">
                  <div className="mb-3 sm:mb-4">{feature.icon}</div>
                  <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{feature.title}</h3>
                  <p className="text-gray-300 text-sm sm:text-base">{feature.description}</p>
                </div>
              ))}
            </div>

            <Link 
              to="/pay-as-you-go"
              className="inline-flex items-center px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-accent-300 text-primary-900 rounded-lg sm:rounded-xl hover:bg-accent-400 transition-colors font-medium shadow-lg transform hover:translate-y-[-2px] text-sm sm:text-base"
            >
              Simuler mon plan
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>

        <div className="text-center">
          <Link 
            to="/products" 
            className="inline-flex items-center px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-primary-600 text-white font-medium rounded-lg sm:rounded-xl transition-colors shadow-lg hover:shadow-xl hover:bg-primary-700 transform hover:translate-y-[-2px] text-sm sm:text-base"
          >
            Voir Toute la Gamme
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </div>
      
      {/* Simulation Modal */}
      <SimulationForm
        isOpen={showSimulation}
        onClose={() => setShowSimulation(false)}
      />
    </section>
  );
};

export default ProductShowcase;